import React, { useState, useMemo } from 'react';
import { Sparkles, Search, SlidersHorizontal, CheckCircle2, Gift } from 'lucide-react';
import { Product } from '../types';
import { ALL_PRODUCTS } from '../data/products';
import { ProductCard } from './ProductCard';

interface ProductCatalogProps {
  onQuickView: (product: Product) => void;
  onOpenCustomOrderModal: () => void;
}

export const ProductCatalog: React.FC<ProductCatalogProps> = ({
  onQuickView,
  onOpenCustomOrderModal,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const categories = [
    { id: 'all', label: 'All $30 Sets' },
    { id: 'pastel-swirls', label: 'Pastel Swirls 🌸' },
    { id: 'gold-sparkle', label: 'Gold & Glam ✨' },
    { id: 'gymnast-chic', label: 'Gymnast Collection 🤸‍♀️' },
    { id: 'custom', label: 'Custom Designs 🎨' },
  ];

  const filteredProducts = useMemo(() => {
    return ALL_PRODUCTS.filter((product) => {
      const matchesCategory =
        selectedCategory === 'all' || product.category === selectedCategory;
      const matchesQuery =
        searchQuery.trim() === '' ||
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.shortDescription.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesCategory && matchesQuery;
    });
  }, [selectedCategory, searchQuery]);

  return (
    <section id="shop" className="py-16 md:py-24 bg-[#FCFAF8] relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-10">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FCE7F3] border border-[#F472B6]/40 text-[#BE185D] text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Hand-Painted Collections</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#292524]">
            Shop Luxury Press-On Nails
          </h2>
          <p className="text-sm sm:text-base text-[#78716C]">
            Every set is individually handcrafted by Kylee. Choose your preferred shape, length, and exact sizing for a perfect salon fit.
          </p>
        </div>

        {/* Free Prep Kit Banner */}
        <div className="mb-10 max-w-4xl mx-auto p-4 rounded-2xl bg-gradient-to-r from-[#FEF9C3]/70 via-[#FCE7F3]/70 to-[#EDE9FE]/70 border border-[#F3C053]/40 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-white text-[#D97706] shadow-xs shrink-0">
              <Gift className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-xs sm:text-sm font-bold text-[#292524]">
                Complimentary $8 Deluxe Prep Kit in Every Order!
              </h4>
              <p className="text-[11px] sm:text-xs text-[#78716C]">
                Includes salon liquid glue, 24 sticky tabs, wooden cuticle pusher, mini buffer, and prep wipes.
              </p>
            </div>
          </div>
          <span className="shrink-0 font-serif font-bold text-sm text-[#B45309] bg-white/90 px-3 py-1.5 rounded-xl shadow-xs border border-[#FDE68A]">
            $30 Flat Price
          </span>
        </div>

        {/* Controls: Category Filter Tabs + Search Input */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-10">
          
          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 scrollbar-none">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-2xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap shrink-0 ${
                  selectedCategory === cat.id
                    ? 'bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-white shadow-md'
                    : 'bg-white text-[#57534E] hover:bg-[#FAF5EE] border border-[#E7DEC8]'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Search Input */}
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search design or color..."
              className="w-full pl-10 pr-4 py-2 rounded-2xl bg-white border border-[#E7DEC8] focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 text-xs sm:text-sm text-[#292524] placeholder:text-[#A8A29E]"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#A8A29E] hover:text-[#292524]"
              >
                Clear
              </button>
            )}
          </div>

        </div>

        {/* Product Grid */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border border-[#EADFCF] p-8 space-y-4">
            <div className="text-4xl text-[#D4AF37]">🔍</div>
            <h3 className="font-serif text-xl font-bold text-[#292524]">No designs found</h3>
            <p className="text-xs text-[#78716C]">
              Try searching with another word, or request a custom bespoke design from Kylee!
            </p>
            <button
              onClick={() => {
                setSelectedCategory('all');
                setSearchQuery('');
              }}
              className="px-4 py-2 rounded-xl text-xs font-bold text-white gold-shimmer-btn"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onQuickView={onQuickView}
              />
            ))}
          </div>
        )}

        {/* Custom Order Callout Footer Card */}
        <div className="mt-16 rounded-3xl bg-gradient-to-r from-[#FEF08A]/40 via-[#FCE7F3]/40 to-[#EDE9FE]/50 p-8 sm:p-10 border border-[#F3C053]/40 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <span className="text-xs font-extrabold uppercase tracking-widest text-[#854D0E]">
              Custom Artistry
            </span>
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#292524]">
              Don't see your dream style?
            </h3>
            <p className="text-xs sm:text-sm text-[#78716C] max-w-xl">
              Kylee loves creating custom sets for gymnastics meets, cheer squads, proms, birthdays, and everyday vibes. Match any outfit or inspiration photo for only $30.
            </p>
          </div>
          <button
            onClick={onOpenCustomOrderModal}
            className="shrink-0 px-8 py-4 rounded-2xl text-white font-bold text-sm gold-shimmer-btn shadow-lg hover:shadow-xl transition-all active:scale-98"
          >
            Design Your Custom Set ($30)
          </button>
        </div>

      </div>
    </section>
  );
};
